import { useState, useEffect, useRef, useCallback } from "react";
import {
  useLocationStatus,
  useToggleLocationSharing,
  useUpdateLocation,
} from "@crm/api";
import { MapPin, MapPinOff, Loader2 } from "lucide-react";
import { cn } from "@crm/utils";
import { toast } from "sonner";

/** Interval in ms between location updates (30 seconds) */
const LOCATION_UPDATE_INTERVAL = 30000;

export function LocationToggle() {
  const { data: status, isLoading: statusLoading } = useLocationStatus();
  const toggleMutation = useToggleLocationSharing();
  const updateMutation = useUpdateLocation();

  const [isTracking, setIsTracking] = useState(false);
  const watchIdRef = useRef<number | null>(null);
  const updateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastPositionRef = useRef<{
    latitude: number;
    longitude: number;
  } | null>(null);

  // Send location update to server
  const sendLocationUpdate = useCallback(
    async (position: GeolocationPosition) => {
      const { latitude, longitude, accuracy } = position.coords;
      lastPositionRef.current = { latitude, longitude };

      try {
        await updateMutation.mutateAsync({ latitude, longitude, accuracy });
      } catch (error) {
        console.error("Failed to update location:", error);
      }
    },
    [updateMutation]
  );

  // Start watching location
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    // Get initial position
    navigator.geolocation.getCurrentPosition(
      sendLocationUpdate,
      (error) => {
        console.error("Geolocation error:", error);
        toast.error("Failed to get your location. Please check permissions.");
      },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 60000 }
    );

    // Watch for position changes
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        lastPositionRef.current = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
      },
      (error) => {
        console.error("Geolocation watch error:", error);
      },
      { enableHighAccuracy: true, maximumAge: 10000 }
    );

    // Send updates at regular intervals
    updateIntervalRef.current = setInterval(() => {
      if (lastPositionRef.current) {
        navigator.geolocation.getCurrentPosition(sendLocationUpdate, () => {}, {
          enableHighAccuracy: true,
          timeout: 5000,
        });
      }
    }, LOCATION_UPDATE_INTERVAL);

    setIsTracking(true);
  }, [sendLocationUpdate]);

  // Stop watching location
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (updateIntervalRef.current !== null) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }
    lastPositionRef.current = null;
    setIsTracking(false);
  }, []);

  // Handle toggle
  const handleToggle = async () => {
    const newState = !status?.is_location_sharing;

    try {
      await toggleMutation.mutateAsync({ enabled: newState });

      if (newState) {
        startTracking();
        toast.success("Location sharing enabled");
      } else {
        stopTracking();
        toast.success("Location sharing disabled");
      }
    } catch (error) {
      toast.error("Failed to toggle location sharing");
    }
  };

  // Initialize tracking state from server
  useEffect(() => {
    if (status?.is_location_sharing && !isTracking) {
      startTracking();
    } else if (!status?.is_location_sharing && isTracking) {
      stopTracking();
    }
  }, [status?.is_location_sharing, isTracking, startTracking, stopTracking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTracking();
    };
  }, [stopTracking]);

  if (statusLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md px-3 py-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  const isSharing = status?.is_location_sharing;
  const isPending = toggleMutation.isPending;

  return (
    <button
      onClick={handleToggle}
      disabled={isPending}
      className={cn(
        "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
        isSharing
          ? "bg-green-100 text-green-700 hover:bg-green-200"
          : "bg-muted text-muted-foreground hover:bg-muted/80"
      )}
      title={
        isSharing
          ? "Click to stop sharing location"
          : "Click to share your location"
      }
    >
      {isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isSharing ? (
        <MapPin className="h-4 w-4" />
      ) : (
        <MapPinOff className="h-4 w-4" />
      )}
      <span className="hidden sm:inline">
        {isSharing ? "Sharing Location" : "Location Off"}
      </span>
    </button>
  );
}
